// @flow
import * as React from "react";
import { connect } from "react-redux";
import posed, { PoseGroup } from "react-pose";
import { rgba } from "polished";
import styled from "styled-components";
import { Link } from "../common/Link";
import Gettext from "../common/Gettext";
import { fontSize } from "../common/styled-mixins";
import { useBonusTimerStorage, useBonusTimerApi } from "../hooks/useBonusTimer";
import { RadialTimer, mapStateToTime } from "../Timers";
import { TourContext } from "../Tour";

import CloseIcon from "@material-ui/icons/CloseOutlined";
import BonusTimerBadge from "../Icons/BonusTimer";
import _InfoIcon from "../Icons/Info";

const Container = styled.div`
  position: absolute;
  bottom: 160px;
  left: 0;
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  align-content: flex-start;
  justify-content: flex-start;
  padding-left: 25px;
  text-decoration: none;
  z-index: 5;
`;

const Badge = styled(Link)`
  display: block;
  position: relative;
  width: 120px;
  height: 120px;
  color: #ffcc00;
  text-decoration: none;
`;

const BadgeLayer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  pointer-events: none;
  user-select: none;
`;

const RadialTimerContainer = styled(BadgeLayer)`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  align-content: center;
  justify-content: center;
  z-index: 2;
`;

const BadgeContent = styled(BadgeLayer)`
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  align-content: center;
  justify-content: center;
  font-weight: 900;
  line-height: 1.2;
  text-transform: uppercase;
  color: #272b34;
  z-index: 3;
`;

const Title = styled.h3`
  max-width: 98px;
  padding-top: 0.25em;
  font-size: ${fontSize(16)};
  text-align: center;
`;

const Percent = styled.p`
  font-size: 24px;
`;

const CloseContainer = styled.div`
  position: absolute;
  top: -2px;
  right: -2px;
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  align-content: center;
  justify-content: center;
  font-size: 13px;
  color: #919191;
  cursor: pointer;
  transition: all 0.3s ease;
  z-index: 4;

  :hover {
    color: ${({ theme }) => theme.foreground};
  }
`;

const TimerContainer = styled.div`
  align-self: flex-start;
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  align-content: center;
  justify-content: center;
  padding: 0 10px;
  padding-top: 5px;
  color: ${rgba("#919191", 0.59)};
`;

const Timer = styled.span`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  align-content: center;
  justify-content: center;
  padding-left: 8px;
  font-size: ${fontSize(11)};
  line-height: 1;
  color: ${({ theme }) => theme.accent};
`;

const InfoIcon = styled(_InfoIcon)``;

const InfoTooltip = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  align-content: center;
  justify-content: center;

  ${InfoIcon} {
    cursor: pointer;
  }
`;

const _Tooltip = posed.div({
  enter: { opacity: 1, transition: { duration: 300 } },
  exit: { opacity: 0, transition: { duration: 300 } }
});

const Tooltip = styled(_Tooltip)`
  position: absolute;
  top: 100%;
  left: -6px;
  transform: translateY(9px);
  min-width: 250px;
  padding: 6px;
  font-size: ${fontSize(12)};
  line-height: 1.2;
  border-radius: 6px;
  background-color: ${rgba("#919191", 0.59)};
  color: #fff;

  ::before {
    content: "";
    position: absolute;
    bottom: 100%;
    left: 7px;
    border: 8px solid transparent;
    border-bottom: 8px solid ${rgba("#919191", 0.59)};
    border-top: none;
  }
`;

type Props = {
  time: number
};

const BonusTimer = ({ time }: Props) => {
  const [showTooltip, toggleTooltip] = React.useState(false);
  const tour = React.useContext(TourContext);
  const [showTimer, toggleTimer] = useBonusTimerStorage();
  const bonus = useBonusTimerApi({ time });

  if (
    showTimer === false ||
    tour !== null ||
    bonus == null ||
    bonus.duration < 0 ||
    bonus.status !== "available"
  ) {
    return null;
  }

  return (
    <Container>
      <CloseContainer onClick={() => toggleTimer(false)}>
        <CloseIcon fontSize="inherit" color="inherit" />
      </CloseContainer>
      <Badge to={`/fillup?promocode=${bonus.promo}`}>
        <BonusTimerBadge />
        <RadialTimerContainer>
          <RadialTimer
            width={102}
            strokeWidth={7}
            progress={bonus.progress}
            full={{ stroke: "rgba(0,0,0,.1)" }}
            stroke="#A34141"
          />
        </RadialTimerContainer>
        <BadgeContent>
          <Title>
            <Gettext>Bonus</Gettext>
          </Title>
          <Percent>+{bonus.percent}%</Percent>
        </BadgeContent>
      </Badge>
      <TimerContainer>
        <InfoTooltip>
          <InfoIcon
            onMouseEnter={() => toggleTooltip(true)}
            onMouseLeave={() => toggleTooltip(false)}
          />
          <PoseGroup>
            {showTooltip && (
              <Tooltip key="tooltip">
                <Gettext>
                  Get your special registration bonus for any deposit before the
                  timer stops counting!
                </Gettext>
              </Tooltip>
            )}
          </PoseGroup>
        </InfoTooltip>
        <Timer>{bonus.ticker}</Timer>
      </TimerContainer>
    </Container>
  );
};

export default connect(mapStateToTime)(BonusTimer);



// WEBPACK FOOTER //
// ./src/components/Chart/BonusTimer.js